import * as Sentry from "@sentry/react";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import Loading from "./components/core/blocks/Loading";
import ErrorLayout from "./layouts/Error";
import { queryClient } from "./config/react-query";
import { store } from "./redux/store";
import { parseSearch, stringifySearch } from "./utils/searchParams";

/** Sidebar applications a route belongs to; `/user` routes outside the user's applications redirect away. */
type Application = "Admin" | "Advertising" | "Merchandising" | "Placement" | "Pricing" | "Product" | "Reports";

export const router = createRouter({
  routeTree,
  context: { store, queryClient },
  parseSearch,
  stringifySearch,
  defaultPendingComponent: () => <Loading fullPage={false} height={300} />,
  defaultErrorComponent: ErrorLayout,
  // Route errors are caught by the router's boundaries, so the app-level Sentry.ErrorBoundary never sees them.
  defaultOnCatch: (error, errorInfo) => {
    Sentry.captureReactException(error, errorInfo);
  },
});

// Signing in or out re-runs the current route's guards, which forward to the redirect target or back to login.
let wasAuthenticated = store.getState().auth.isAuthenticated;
store.subscribe(() => {
  const { isAuthenticated } = store.getState().auth;
  if (isAuthenticated === wasAuthenticated) return;
  wasAuthenticated = isAuthenticated;
  void router.invalidate();
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface StaticDataRouteOption {
    application?: Application;
  }

  /** Navigation state pages read via `useLocation().state`. */
  interface HistoryState {
    // The pricing dashboards.
    firstVisit?: boolean;
    /** Form values ProgramCreate starts from when cloning a program. */
    defaults?: Record<string, unknown>;
  }
}
